import { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Clock, Users, User, Phone, MessageSquare, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const timeSlots = [
  '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM',
  '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM',
  '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM',
  '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM',
  '8:00 PM', '8:30 PM', '9:00 PM', '9:30 PM',
  '10:00 PM', '10:30 PM', '11:00 PM'
];

export const BookingPage = () => {
  const [date, setDate] = useState();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    time: '',
    guests: '2',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.phone || !date || !formData.time) {
      toast.error('Please fill all required fields');
      return;
    }

    // Save to localStorage (mock)
    const bookings = JSON.parse(localStorage.getItem('cafeBookings') || '[]');
    const newBooking = {
      id: Date.now(),
      ...formData,
      date: format(date, 'PPP'),
      status: 'pending',
      createdAt: new Date().toISOString()
    };
    bookings.push(newBooking);
    localStorage.setItem('cafeBookings', JSON.stringify(bookings));

    setSubmitted(true);
    toast.success('Booking request submitted successfully!');

    // Send WhatsApp notification
    const phoneNumber = '919994520043';
    const message = `New Booking Request:\n\nName: ${formData.name}\nPhone: ${formData.phone}\nDate: ${format(date, 'PPP')}\nTime: ${formData.time}\nGuests: ${formData.guests}\nMessage: ${formData.message || 'None'}`;
    setTimeout(() => {
      window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    }, 1000);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="section-container py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="mb-6 flex justify-center">
              <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-success" />
              </div>
            </div>
            <h1 className="text-4xl font-display font-bold text-foreground mb-4">
              Booking Request Submitted!
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Thank you for your booking request. Our team will call you shortly to confirm your reservation.
            </p>
            <div className="bg-muted rounded-xl p-6 mb-8">
              <h3 className="font-semibold text-lg mb-3">Booking Details</h3>
              <div className="space-y-2 text-left">
                <p><strong>Name:</strong> {formData.name}</p>
                <p><strong>Phone:</strong> {formData.phone}</p>
                <p><strong>Date:</strong> {format(date, 'PPP')}</p>
                <p><strong>Time:</strong> {formData.time}</p>
                <p><strong>Guests:</strong> {formData.guests}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button onClick={() => setSubmitted(false)} variant="outline">
                Make Another Booking
              </Button>
              <Button onClick={() => window.location.href = '/'} className="btn-primary">
                Back to Home
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Page Header */}
      <div className="bg-primary text-primary-foreground py-16">
        <div className="section-container text-center">
          <h1 className="text-4xl sm:text-5xl font-display font-bold mb-4">Book a Seat</h1>
          <p className="text-lg text-primary-foreground/90 max-w-2xl mx-auto">
            Reserve your spot at Insta Cafe for a delightful experience
          </p>
        </div>
      </div>

      {/* Booking Form */}
      <div className="section-container py-16">
        <div className="max-w-3xl mx-auto">
          <div className="cafe-card p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <Label htmlFor="name" className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4" />
                  Full Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone" className="flex items-center gap-2 mb-2">
                  <Phone className="w-4 h-4" />
                  Phone Number *
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                  required
                />
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Date */}
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <CalendarIcon className="w-4 h-4" />
                    Select Date *
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, 'PPP') : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          return date < today;
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Time */}
                <div>
                  <Label htmlFor="time" className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4" />
                    Select Time *
                  </Label>
                  <select
                    id="time"
                    name="time"
                    value={formData.time}
                    onChange={handleChange}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-foreground"
                    required
                  >
                    <option value="">Select time</option>
                    {timeSlots.map((slot) => (
                      <option key={slot} value={slot}>{slot}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Number of Guests */}
              <div>
                <Label htmlFor="guests" className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4" />
                  Number of Guests
                </Label>
                <Input
                  id="guests"
                  name="guests"
                  type="number"
                  min="1"
                  max="20"
                  value={formData.guests}
                  onChange={handleChange}
                  placeholder="Number of people"
                />
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message" className="flex items-center gap-2 mb-2">
                  <MessageSquare className="w-4 h-4" />
                  Special Requests (Optional)
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Any special requests or preferences?"
                  rows={4}
                />
              </div>

              {/* Submit Button */}
              <Button type="submit" size="lg" className="w-full btn-primary">
                Submit Booking Request
              </Button>

              <p className="text-sm text-muted-foreground text-center">
                * Our team will call you to confirm your booking
              </p>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BookingPage;